import { Loader2, Upload, X } from 'lucide-react';
import { type ChangeEvent, type DragEvent, useRef, useState } from 'react';
import logoColor from '@/assets/brand/signcare-logo.svg';
import { toast } from '@/lib/notify';
import { cn } from '@/lib/utils';

import { useRemoveLogo, useUploadLogo } from '../api/useLogo';

// Organization logo upload (designer: `.sc-cust-drawer__upload-box`). Shared by
// the /customization page and the workflow Customization drawer.
//
// The logo is an ORGANIZATION-level resource — one image in object storage, not
// part of the per-workflow colour blob — so uploading here changes it for every
// workflow. `scopeNote` lets the caller say so where that isn't obvious.
//
// Upload/remove take effect immediately (they're their own resource, independent
// of the colours' Save button), which is why this owns its own mutations.

const MAX_BYTES = 2 * 1024 * 1024;
const ACCEPTED = ['image/png', 'image/jpeg', 'image/jpg'];

export function OrganizationLogoField({
  logoUrl,
  disabled = false,
  scopeNote,
  showPreviewBox = true,
}: {
  logoUrl: string | null;
  disabled?: boolean;
  /** Extra line under the drop zone, e.g. that the logo is shared org-wide. */
  scopeNote?: string;
  /** Render the designer's separate "Preview Logo" tile beside the drop zone. */
  showPreviewBox?: boolean;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const uploadLogo = useUploadLogo();
  const removeLogo = useRemoveLogo();
  const busy = disabled || uploadLogo.isPending || removeLogo.isPending;

  // Client-side guard only — the backend re-validates type via magic bytes.
  function handleFile(file: File) {
    if (file.size > MAX_BYTES) {
      toast.error('Logo must be 2MB or smaller.');
      return;
    }
    if (!ACCEPTED.includes(file.type)) {
      toast.error('Logo must be a PNG or JPEG image.');
      return;
    }
    uploadLogo.mutate(file, {
      onSuccess: () => toast.success('Logo updated.'),
      onError: () => toast.error('Could not upload the logo. Please try again.'),
    });
  }

  function onDrop(e: DragEvent) {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }

  function onFileInput(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = '';
  }

  function handleRemove() {
    removeLogo.mutate(undefined, {
      onSuccess: () => toast.success('Logo removed.'),
      onError: () => toast.error('Could not remove the logo. Please try again.'),
    });
  }

  const dropZone = (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium text-gray30">Organization Logo</span>
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg"
        hidden
        onChange={onFileInput}
      />
      {/* The clear button is a SIBLING of the drop zone, not a child: the drop
          zone is itself a <button>, and nesting buttons is invalid HTML. */}
      <div className="relative">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          onDragEnter={(e) => {
            e.preventDefault();
            setDragActive(true);
          }}
          onDragOver={(e) => e.preventDefault()}
          onDragLeave={() => setDragActive(false)}
          onDrop={onDrop}
          className={cn(
            'flex h-[120px] w-full cursor-pointer items-center justify-center rounded-[12px] border-[1.5px] border-dashed p-4 text-center transition-all disabled:cursor-not-allowed disabled:opacity-60',
            dragActive
              ? 'border-primary bg-primary/5'
              : 'border-placeholder hover:border-primary hover:bg-primary/[0.02]',
          )}
        >
          {/* With a logo uploaded, the zone shows it — click to replace. Without
              one, the upload prompt. Either way the whole box stays clickable. */}
          {logoUrl && !uploadLogo.isPending ? (
            <div className="flex flex-col items-center gap-2">
              <img
                src={logoUrl}
                alt="Organization logo"
                className="max-h-[52px] max-w-[180px] object-contain"
              />
              <p className="m-0 text-[10px] text-gray66">Click to replace, or use ✕ to remove</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#F4F4F4]">
                {uploadLogo.isPending ? (
                  <Loader2 size={16} className="animate-spin text-primary" />
                ) : (
                  <Upload size={16} className="text-gray66" />
                )}
              </div>
              <p className="m-0 text-sm text-gray66">
                Drag and Drop or <span className="font-semibold text-primary">Click to upload</span>
              </p>
              <p className="m-0 max-w-[250px] text-[10px] leading-tight text-[#A5A5A5]">
                Recommended size: 500x500px. Max file size: 2MB. Supported formats: PNG, JPG.
              </p>
            </div>
          )}
        </button>
        {logoUrl && (
          <button
            type="button"
            onClick={handleRemove}
            disabled={busy}
            aria-label="Remove organization logo"
            className="absolute -right-2 -top-2 flex h-[22px] w-[22px] items-center justify-center rounded-full border border-grayEF bg-[#F4F6FA] text-gray30 transition-colors hover:bg-grayEF hover:text-black-logo disabled:opacity-50"
          >
            {removeLogo.isPending ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <X size={12} />
            )}
          </button>
        )}
      </div>
      {scopeNote && <p className="m-0 text-[10px] leading-tight text-gray66">{scopeNote}</p>}
    </div>
  );

  if (!showPreviewBox) return dropZone;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      {dropZone}
      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium text-gray30">Preview Logo</span>
        <div className="relative flex h-[120px] flex-col items-center justify-center rounded-[12px] border-[1.5px] border-dashed border-placeholder p-4">
          {logoUrl && (
            <button
              type="button"
              onClick={handleRemove}
              disabled={busy}
              aria-label="Remove logo"
              className="absolute right-2 top-2 flex h-[22px] w-[22px] items-center justify-center rounded-full bg-[#f1f5f9] text-[#475569] transition-colors hover:bg-[#e2e8f0] hover:text-[#0f172a] disabled:opacity-50"
            >
              <X size={14} />
            </button>
          )}
          {/* No logo yet → the SignCare mark, which is what a session would
              actually show, rather than an empty tile. */}
          <img
            src={logoUrl || logoColor}
            alt="Brand logo preview"
            className="max-h-[38px] max-w-[140px] object-contain"
          />
        </div>
      </div>
    </div>
  );
}
